import { createColumnHelper } from '@tanstack/react-table';

import type { FleetDeviceWithMember } from 'src/api/endpoints/cloudEnterprise.types';
import { TimeSinceNow } from 'src/components/TimeSinceNow';
import { createSelectColumn } from 'src/utils/createTableSelectColumn';
import { deviceHelpers } from 'src/utils/deviceHelpers';

import { DotsThreeVertical } from '@remarkable/ark-icons';
import { Button, Tooltip } from 'src/components';
import { sortByDate } from 'src/utils/sortByDate';
import { CellDevice } from './CellDevice';
import { CellUser } from './CellUser';
import { ComplianceStatus } from './ComplianceStatus';
import { DeviceMenu } from './DeviceMenu';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';
import { fleetUserHelpers } from './fleetUserHelpers';

const columnHelper = createColumnHelper<FleetDeviceWithMember>();

export const deviceTableColumns = [
  createSelectColumn(columnHelper),
  columnHelper.accessor(
    (data) =>
      [
        deviceHelpers.getDeviceName(data.device),
        deviceHelpers.getDeviceType(data.device),
        data.device.serialNumber,
      ].join('|'),
    {
      id: 'devices',
      header: 'Devices',
      meta: {
        colWidth: 'w-1/5',
        fullWidth: true,
        truncate: false,
      },
      getUniqueValues: (data) => {
        return [deviceHelpers.getDeviceName(data.device)];
      },
      cell: (info) => <CellDevice device={info.row.original.device} />,
      filterFn: (row, _, value: string[]) => {
        const deviceName = deviceHelpers.getDeviceName(row.original.device);
        return value.includes(deviceName);
      },
    }
  ),

  // Make MAC address searchable
  columnHelper.accessor('device.macAddress', {
    id: 'device-mac-address',
  }),

  columnHelper.accessor(
    (d) => fleetMemberRowHelpers.getDeviceLastSeenDate(d.device),
    {
      header: 'Last seen',
      meta: {
        colWidth: 'w-1/6',
      },
      sortingFn: (a, b) => {
        return sortByDate(
          a.original.device.lastSeen,
          b.original.device.lastSeen
        );
      },
      cell: (info) => <TimeSinceNow date={info.getValue() ?? undefined} />,
    }
  ),

  columnHelper.accessor(
    (data) => fleetUserHelpers.getDeviceVerificationStatus(data.device),
    {
      id: 'device-verified',
      header: 'Verification',
      filterFn: (row, _, value: string[]) => {
        const verificationStatus = fleetUserHelpers.getDeviceVerificationStatus(
          row.original.device
        );
        return value.includes(verificationStatus);
      },
      cell: (info) => {
        switch (info.getValue()) {
          case 'Verified':
            return (
              <Tooltip title="Device is verified" asChild>
                <div className="cursor-help">
                  <div className="mr-8 inline-block size-8 rounded-full bg-feedback-green-500" />
                  Verified
                </div>
              </Tooltip>
            );
          case 'Confirmed':
            return (
              <Tooltip
                title={
                  <p className="max-w-320 leading-16">
                    This device is operating with standard software security.
                    Hardware verification is exclusive to reMarkable Paper Pro
                    and newer models.
                  </p>
                }
                asChild
              >
                <div className="cursor-help">
                  <div className="mr-8 inline-block size-8 rounded-full bg-feedback-green-500" />
                  Confirmed
                </div>
              </Tooltip>
            );
          case 'Not verified': {
            if (
              deviceHelpers.isLegacySecurityDevice(info.row.original.device)
            ) {
              return (
                <Tooltip
                  title={
                    <p className="max-w-320 leading-16">
                      The system is unable to verify the authenticity of this
                      device. Ensure the device is connected to Wi-Fi and
                      running the latest software. If the issue persists, try
                      restarting the device.
                    </p>
                  }
                  asChild
                >
                  <div className="cursor-help rounded-full py-2 interface-sm whitespace-nowrap shadow-none">
                    <div className="mr-8 inline-block size-8 rounded-full bg-feedback-red-500" />
                    Failed
                  </div>
                </Tooltip>
              );
            }
            return (
              <Tooltip
                title={
                  <p className="max-w-320 leading-16">
                    The system can&apos;t verify the authenticity of this
                    device. Check that Developer mode is turned off, the device
                    is connected to Wi-Fi, and that the software is up to date.
                    If the issue persists, try restarting it.
                  </p>
                }
                asChild
              >
                <div className="cursor-help rounded-full py-2 interface-sm whitespace-nowrap shadow-none">
                  <div className="mr-8 inline-block size-8 rounded-full bg-feedback-red-500" />
                  Failed
                </div>
              </Tooltip>
            );
          }
          case 'Unavailable':
          default:
            return (
              <Tooltip
                title={
                  <p className="max-w-320 leading-16">
                    The system can&apos;t currently determine the verification
                    status of this device. Check that the device is connected to
                    Wi-Fi and running the latest software, or try restarting it.
                  </p>
                }
                asChild
              >
                <div className="cursor-help rounded-full py-2 interface-sm shadow-none">
                  <div className="mr-8 inline-block size-8 rounded-full bg-feedback-orange-500" />
                  Unavailable
                </div>
              </Tooltip>
            );
        }
      },
    }
  ),

  columnHelper.accessor((data) => data.device.softwareVersion ?? '', {
    id: 'device-software-versions',
    header: 'Software version',
    getUniqueValues: (data) => {
      const [major, minor] = data.device.softwareVersion?.split('.') || [];
      return [[major, minor].join('.')];
    },
    filterFn: (row, _, value: string[]) => {
      const softwareVersion = row.original.device.softwareVersion ?? '';
      return value.some((v) => softwareVersion.startsWith(v));
    },
  }),

  columnHelper.accessor(
    (d) => fleetUserHelpers.getDeviceComplianceStatus(d.device),
    {
      id: 'compliance',
      header: 'Compliance',
      meta: {
        colWidth: 'w-1/6',
      },
      cell: (info) => {
        return (
          <ComplianceStatus
            status={info.row.original.device.policyCompliance?.status}
          />
        );
      },
      getUniqueValues: (data) => {
        return [fleetUserHelpers.getDeviceComplianceStatus(data.device)];
      },
      filterFn: (row, _, value: string[]) => {
        const complianceStatus = fleetUserHelpers.getDeviceComplianceStatus(
          row.original.device
        );
        return value.includes(complianceStatus);
      },
    }
  ),

  columnHelper.accessor(
    (d) => fleetUserHelpers.getName(d.user) + fleetUserHelpers.getEmail(d.user),
    {
      header: 'User',
      meta: {
        truncate: true,
      },
      cell: (info) => {
        return <CellUser user={info.row.original.user} />;
      },
    }
  ),

  columnHelper.display({
    id: 'edit',
    header: 'Edit',
    meta: {
      align: 'center',
      colWidth: 'w-[50px]',
      truncate: false,
    },
    cell: ({ row }) => (
      <DeviceMenu
        device={row.original.device}
        userId={row.original.user.id}
        userEmail={row.original.user.email}
      >
        <Button
          variant="tertiary-neutral"
          size="small"
          className="!rounded-sm data-[state=open]:bg-paper-3"
          onClick={(e) => e.stopPropagation()}
        >
          <DotsThreeVertical />
        </Button>
      </DeviceMenu>
    ),
  }),
];
